import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const audioRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
    }

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setProgress(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      // Auto-play next track if available
      if (currentIndex < playlist.length - 1) {
        playTrackAtIndex(currentIndex + 1);
      } else {
        setIsPlaying(false);
        setProgress(0);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentIndex, playlist]);

  const playTrack = (track, trackList = []) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Construct full URL
    const apiUrl = window.location.origin;
    const audioUrl = track.audio_url?.startsWith('http') 
      ? track.audio_url 
      : `${apiUrl}${track.audio_url}`;

    if (currentTrack?.id === track.id) {
      // Toggle play/pause for same track
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    } else {
      // Play new track
      if (trackList.length > 0) {
        setPlaylist(trackList);
        const idx = trackList.findIndex(t => t.id === track.id);
        setCurrentIndex(idx >= 0 ? idx : 0);
      }
      
      setCurrentTrack(track);
      audio.src = audioUrl;
      audio.volume = volume;
      audio.muted = isMuted;
      audio.play().catch(err => console.error('Audio play error:', err));
    }
  };

  const playTrackAtIndex = (index) => {
    if (index >= 0 && index < playlist.length) {
      const track = playlist[index];
      setCurrentIndex(index);
      setCurrentTrack(track);
      
      const audio = audioRef.current;
      const apiUrl = window.location.origin;
      const audioUrl = track.audio_url?.startsWith('http') 
        ? track.audio_url 
        : `${apiUrl}${track.audio_url}`;
      
      audio.src = audioUrl;
      audio.play().catch(err => console.error('Audio play error:', err));
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const skipNext = () => {
    if (currentIndex < playlist.length - 1) {
      playTrackAtIndex(currentIndex + 1);
    }
  };

  const skipPrevious = () => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      // If more than 3 seconds in, restart current track
      audio.currentTime = 0;
    } else if (currentIndex > 0) {
      playTrackAtIndex(currentIndex - 1);
    }
  };

  const seek = (time) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
    }
  };

  const seekPercent = (percent) => {
    const audio = audioRef.current;
    if (audio && duration) {
      audio.currentTime = (percent / 100) * duration;
    }
  };

  const setVolumeLevel = (level) => {
    const audio = audioRef.current;
    setVolume(level);
    if (audio) {
      audio.volume = level;
      audio.muted = level === 0;
      setIsMuted(level === 0);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audio) {
      audio.muted = newMuted;
    }
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setCurrentTrack(null);
    setIsPlaying(false);
    setProgress(0);
  };

  const value = {
    // State
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    playlist,
    currentIndex,
    
    // Actions
    playTrack,
    togglePlay,
    skipNext,
    skipPrevious,
    seek,
    seekPercent,
    setVolumeLevel,
    toggleMute,
    stopPlayback,
    setPlaylist,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;
